import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    CircularProgress,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import FilterListIcon from '@material-ui/icons/FilterList';
import HistoryIcon from '@material-ui/icons/History';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import PersonIcon from '@material-ui/icons/Person';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import { baseUrl } from '../../util/host';
import axios from 'axios';
import moment from 'moment-timezone';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#f5f7fa',
        minHeight: 'calc(100vh - 64px)',
    },
    header: {
        marginBottom: theme.spacing(3),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    titleIcon: {
        color: '#3f51b5',
        fontSize: 36,
    },
    title: {
        fontSize: '28px',
        fontWeight: 600,
        color: '#1a1a1a',
        letterSpacing: '-0.5px',
    },
    subtitle: {
        fontSize: '14px',
        color: '#666',
        marginTop: theme.spacing(0.5),
    },
    actionBar: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    searchField: {
        backgroundColor: 'white',
        borderRadius: theme.spacing(1),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#3f51b5',
            },
        },
    },
    refreshButton: {
        backgroundColor: 'white',
        color: '#3f51b5',
        textTransform: 'none',
        padding: theme.spacing(1, 2),
        fontSize: '14px',
        fontWeight: 500,
        border: '1px solid #e0e0e0',
        '&:hover': {
            backgroundColor: '#f5f7fa',
            borderColor: '#3f51b5',
        },
    },
    tableContainer: {
        boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
    },
    tableHead: {
        backgroundColor: '#f8f9fb',
    },
    tableHeaderCell: {
        fontWeight: 600,
        fontSize: '13px',
        color: '#5a5a5a',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: theme.spacing(2, 3),
        borderBottom: '2px solid #e0e0e0',
    },
    tableRow: {
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f8f9fb',
            transform: 'translateY(-1px)',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
        },
        '&:not(:last-child)': {
            borderBottom: '1px solid #f0f0f0',
        },
    },
    tableCell: {
        padding: theme.spacing(2.5, 3),
        fontSize: '14px',
        color: '#333',
        verticalAlign: 'middle',
    },
    serialNumber: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#3f51b5',
        backgroundColor: '#e8eaf6',
        borderRadius: '50%',
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    userIcon: {
        color: '#3f51b5',
        fontSize: 20,
    },
    userEmail: {
        fontSize: '14px',
        color: '#333',
        fontWeight: 500,
        wordBreak: 'break-word',
    },
    noEmail: {
        color: '#999',
        fontStyle: 'italic',
    },
    linkSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    downloadButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: '13px',
        color: '#3f51b5',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: 500,
        padding: theme.spacing(0.75, 1.5),
        borderRadius: theme.spacing(0.5),
        border: '1px solid #e0e0e0',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#e8eaf6',
            borderColor: '#3f51b5',
            transform: 'translateX(2px)',
        },
    },
    successButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: '13px',
        color: '#4caf50',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: 500,
        padding: theme.spacing(0.75, 1.5),
        borderRadius: theme.spacing(0.5),
        border: '1px solid #e0e0e0',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#e8f5e9',
            borderColor: '#4caf50',
            transform: 'translateX(2px)',
        },
    },
    downloadIcon: {
        fontSize: 18,
    },
    dateTimeContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(0.5),
    },
    dateText: {
        fontSize: '14px',
        color: '#333',
        fontWeight: 500,
    },
    timeText: {
        fontSize: '12px',
        color: '#999',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(10),
        color: '#999',
        backgroundColor: 'white',
        borderRadius: theme.spacing(1),
        boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
    },
    emptyIcon: {
        fontSize: 80,
        color: '#e0e0e0',
        marginBottom: theme.spacing(2),
    },
    loadingContainer: {
        textAlign: 'center',
        padding: theme.spacing(10),
        backgroundColor: 'white',
        borderRadius: theme.spacing(1),
        boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
    },
    statsContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
    },
    statCard: {
        flex: 1,
        minWidth: 200,
        padding: theme.spacing(2),
        backgroundColor: 'white',
        borderRadius: theme.spacing(1),
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    statIcon: {
        fontSize: 40,
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1),
    },
    statContent: {
        flex: 1,
    },
    statValue: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#1a1a1a',
    },
    statLabel: {
        fontSize: '13px',
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
}));

const ImportHistory = () => {
    const classes = useStyles();
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

  

    const fetchImportHistoryData = () => {
        setLoading(true);
        axios.get(`${baseUrl("REACT_APP_API_URL")}/api/sla/import-history`)
            .then((res) => {
                setHistoryData(res.data.data || []);
                setLoading(false);
            })
            .catch((err) => {
                console.log('error fetching import history data', err);
                setLoading(false);
            });
    };

    const handleDownloadFile = (link) => {
        window.open(link, '_blank');
    };

    const handleRefresh = () => {
        fetchImportHistoryData();
    };

    const filteredData = historyData.filter(item => 
        !searchQuery || 
        item.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        moment(item.created_at).format('DD/MM/YYYY').includes(searchQuery)
    );
    useEffect(() => {
        fetchImportHistoryData();
    }, []);

    return (
        <div className={classes.root}>
            {/* Page Header */}
            <Box>
                <Box className={classes.header}>
                    <Box>
                        <Box className={classes.titleContainer}>
                            <HistoryIcon className={classes.titleIcon} />
                            <Typography className={classes.title}>
                                Import History
                            </Typography>
                        </Box>
                        <Typography className={classes.subtitle}>
                            View and download your previous import records
                        </Typography>
                    </Box>
                    
                    <Box className={classes.actionBar}>
                        <TextField
                            className={classes.searchField}
                            placeholder="Search by email or date..."
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <Tooltip title="Refresh">
                            <IconButton
                                className={classes.refreshButton}
                                onClick={handleRefresh}
                                size="small"
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Stats Cards */}
                {!loading && historyData.length > 0 && (
                    <Box className={classes.statsContainer}>
                        <Paper className={classes.statCard}>
                            <CloudUploadIcon 
                                className={classes.statIcon} 
                                style={{ color: '#3f51b5', backgroundColor: '#e8eaf6' }}
                            />
                            <Box className={classes.statContent}>
                                <Typography className={classes.statValue}>
                                    {historyData.length}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    Total Imports
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper className={classes.statCard}>
                            <AssignmentTurnedInIcon 
                                className={classes.statIcon} 
                                style={{ color: '#4caf50', backgroundColor: '#e8f5e9' }}
                            />
                            <Box className={classes.statContent}>
                                <Typography className={classes.statValue}>
                                    {historyData.filter(h => h.email).length}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    With User Info
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* History Table */}
            {loading ? (
                <Box className={classes.loadingContainer}>
                    <CircularProgress size={60} />
                    <Typography style={{ marginTop: 16, color: '#999' }}>
                        Loading import history...
                    </Typography>
                </Box>
            ) : filteredData && filteredData.length > 0 ? (
                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table>
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell className={classes.tableHeaderCell} align="center" style={{ width: '5%' }}>
                                    #
                                </TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '25%' }}>
                                    Imported By
                                </TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ width: '45%' }}>
                                    Files
                                </TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right" style={{ width: '25%' }}>
                                    Date & Time
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((item, index) => (
                                <TableRow key={item._id || index} className={classes.tableRow}>
                                    {/* Serial Number */}
                                    <TableCell className={classes.tableCell} align="center">
                                        <Box className={classes.serialNumber}>
                                            {index + 1}
                                        </Box>
                                    </TableCell>

                                    {/* User Email */}
                                    <TableCell className={classes.tableCell}>
                                        <Box className={classes.userInfo}>
                                            <PersonIcon className={classes.userIcon} />
                                            <Typography className={item.email ? classes.userEmail : `${classes.userEmail} ${classes.noEmail}`}>
                                                {item.email || 'Unknown User'}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    {/* Download Links */}
                                    <TableCell className={classes.tableCell}>
                                        <Box className={classes.linkSection}>
                                            <Link
                                                className={classes.downloadButton}
                                                onClick={() => handleDownloadFile(item.uploaded_file)}
                                                component="button"
                                            >
                                                <CloudUploadIcon className={classes.downloadIcon} />
                                                Uploaded File
                                            </Link>
                                            
                                            <Link
                                                className={classes.successButton}
                                                onClick={() => handleDownloadFile(item.success_file)}
                                                component="button"
                                            >
                                                <AssignmentTurnedInIcon className={classes.downloadIcon} />
                                                Result File
                                            </Link>
                                        </Box>
                                    </TableCell>

                                    {/* Date and Time */}
                                    <TableCell className={classes.tableCell} align="right">
                                        <Box className={classes.dateTimeContainer}>
                                            <Typography className={classes.dateText}>
                                                {moment(item.created_at || new Date()).format('DD MMM YYYY')}
                                            </Typography>
                                            <Typography className={classes.timeText}>
                                                {moment(item.created_at || new Date()).format('HH:mm:ss')}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Paper className={classes.emptyState}>
                    <HistoryIcon className={classes.emptyIcon} />
                    <Typography variant="h6" style={{ color: '#666', marginBottom: 8 }}>
                        No import history found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {searchQuery ? 'Try adjusting your search query' : 'Your import history will appear here after you upload files'}
                    </Typography>
                </Paper>
            )}
        </div>
    );
};

export default ImportHistory;